import { convertToISO8601 } from "../../../time/convert";
import { ScheduleOption, DayPart, ScheduleOptionInput, PlaydateInviteSuggestion, PlaydateType, ScheduleOptionType, ParticipantSummary } from "../../../gql/types";
import { SelectedDayPartPair, ProposedDate, DayPartSelection, TimeSelectionRow } from "../../../components/time/types";
import { DraftPlaydateInvite } from "components/playdate/types";
import { DateTime } from "luxon";

export function convertScheduleOptions(
    scheduleOptions: ScheduleOption[]
): SelectedDayPartPair[] {
    return scheduleOptions
        // Filter out any that lack date or dayPart
        .filter((opt) => opt.date != null && opt.dayPart != null)
        // Map into the shape we need
        .map((opt) => ({
            date: opt.date!.split("T")[0],    // or format this if needed
            dayPart: opt.dayPart as DayPart,  // cast if needed
        }));
}

export function convertScheduleOptionsToProposedDates(
    scheduleOptions: ScheduleOption[]
): ProposedDate[] {
    return scheduleOptions
        // Only keep valid items
        .filter((opt) => opt.date != null && opt.dayPart != null)
        // Map to ProposedDate
        .map((opt) => ({
            dateString: opt.date as string,    // parse or format if needed
            dayPart: opt.dayPart as DayPart,
            // bannerText: 'Most Popular',    // set if you have logic for this
            // recommendation: ...,           // set if you have logic for this
        }));
}


export function convertScheduleOptionsToDayPartSelections(
    scheduleOptions: ScheduleOption[]
): DayPartSelection[] {
    return scheduleOptions
        // Only keep valid items
        .filter((opt) => opt.date != null && opt.dayPart != null)
        // Map to ProposedDate
        .map((opt) => ({
            selectedDate: opt.date as string,    // parse or format if needed
            selectedDayPart: opt.dayPart as DayPart,
            // bannerText: 'Most Popular',    // set if you have logic for this
            // recommendation: ...,           // set if you have logic for this
        }));
}

// Type guard to determine if an item is a DayPartSelection.
function isDayPartSelection(item: any): item is DayPartSelection {
    return item && typeof item.selectedDate === "string";
}

/**
 * Converts an array of user selections into an array of ScheduleOptionInput.
 * It follows two different flows based on the type of selection:
 * - For DayPartSelection, it maps using the selectedDate/selectedDayPart properties.
 * - For TimeSelectionRow, it uses date/dayPart (and associated fields) directly.
 */
export function convertUserSelectionToScheduleOptions(
    selection: DayPartSelection[] | TimeSelectionRow[]
): Array<ScheduleOptionInput> {
    if (selection.length === 0) return [];

    if (isDayPartSelection(selection[0])) {
        // Conversion flow for DayPartSelection
        return selection.map((item) => {
            const castItem = item as DayPartSelection;
            return (
                {

                    date: castItem.selectedDate,
                    dayOfWeek: null, // Optionally, derive dayOfWeek from selectedDate if needed.
                    dayPart: castItem.selectedDayPart,
                    startTime: null,
                    endTime: null,
                })
        });
    } else {
        return selection.map((item) => {
            const castItem = item as TimeSelectionRow;
            return (
                {

                    date: castItem.date,
                    dayOfWeek: null, // Optionally, derive dayOfWeek from selectedDate if needed.
                    dayPart: null,
                    startTime: castItem.startTime,
                    endTime: castItem.endTime,
                })
        });
    }
}

export function convertDayPartSelectionToScheduleOptionInputs(
    selection: DayPartSelection[]
): Array<ScheduleOptionInput> {
    return selection.map((item: DayPartSelection) => ({
        date: convertToISO8601(item.selectedDate) || null,
        dayPart: item.selectedDayPart || null,
    }));
}

export function convertSelectedDayPartsToScheduleOptionInputs(
    selection: SelectedDayPartPair[]
): ScheduleOptionInput[] {
    return selection.map((item: SelectedDayPartPair) => {
        return (
            {
                date: convertToISO8601(item.date),
                dayPart: item.dayPart,
                dayOfWeek: null,
                startTime: null,
                endTime: null,
            });
    });
}

export function convertSuggestionToDraftInvite(
    suggestion: PlaydateInviteSuggestion | null,
    organizerID: string,
    initialConnections?: ParticipantSummary[],
    fallbackDate?: string
): DraftPlaydateInvite {
    if (suggestion === null) {
        return {
            title: "",
            location: undefined,
            playdateType: PlaydateType.Private,
            optionType: ScheduleOptionType.OneTime,
            scheduleOptions: fallbackDate ? [{
                date: toDateOnlyString(fallbackDate),
                dayPart: DayPart.EarlyAfternoon,
            }] : [],
            scheduleReason: undefined,
            rsvpDeadline: undefined,
            details: undefined,
            guestConnections: initialConnections ?? [],
            externalGuests: [],
            organizerID,
        };
    }

    const scheduleSuggestion = suggestion.scheduleSuggestion;

    let options = scheduleSuggestion?.scheduleOptions
        ? convertScheduleOptions(scheduleSuggestion.scheduleOptions)
        : [];

    if (fallbackDate) {
        const fallbackDateOnly = toDateOnlyString(fallbackDate);
        if (!options.some((opt) => {
            try {
                return opt.date === fallbackDateOnly;
            } catch {
                return false;
            }
        })
        ) {
            options.push({
                date: fallbackDateOnly,
                dayPart: DayPart.EarlyAfternoon,
            });
        }
    }

    return {
        title: suggestion.textualSuggestion?.title ?? "",
        location: suggestion.textualSuggestion?.location ?? "",
        playdateType: PlaydateType.Private,
        optionType: ScheduleOptionType.OneTime,
        scheduleOptions: options,
        scheduleReason: scheduleSuggestion?.reason ?? undefined,
        rsvpDeadline: undefined,
        details: suggestion.textualSuggestion?.details ?? undefined,
        guestConnections: initialConnections ?? [],
        externalGuests: [],
        organizerID,
    };
}

function toDateOnlyString(isoString: string): string {
    return DateTime.fromISO(isoString).toFormat("yyyy-MM-dd");
}

export function inferDayPart(startTime: string): DayPart | null {
    const hour = parseInt(startTime.split(":")[0], 10);
    if (hour >= 5 && hour < 11) return DayPart.Morning;
    if (hour >= 11 && hour < 15) return DayPart.EarlyAfternoon;
    if (hour >= 15 && hour < 19) return DayPart.LateAfternoon;
    if (hour >= 19 || hour < 5) return DayPart.Evening;
    return null;
}

export function convertJSONtoScheduleOptions(input: { scheduleOptions: any[] }) {
    const normalizedOptions: ScheduleOption[] = input.scheduleOptions.flatMap((option) => {
        const { date, dayPart, startTime, endTime } = option;
        console.log("Processing schedule option:", option);
        const now = DateTime.utc().startOf("day");
        const yesterday = now.minus({ days: 1 });

        if (!date || typeof date !== "string") {
            console.warn("Missing or invalid date in schedule option:", option);
            return [];
        }

        // Parse the date
        console.debug("Trying to parse raw date string:", date);
        let parsedDate = DateTime.fromISO(date, { zone: "utc" });
        if (!parsedDate.isValid) {
            console.warn("Invalid ISO date format in schedule option:", option);
            console.warn("Luxon reason:", parsedDate.invalidReason);
            return [];
        }

        // Filter out suggestions older than yesterday
        if (parsedDate < yesterday) {
            console.warn("Skipping outdated date:", parsedDate.toISO());
            return [];
        }
    
        // Get DayPart
        const inferredDayPart = dayPart ?? (startTime && endTime ? inferDayPart(startTime) : null);

        if (!inferredDayPart) {
            console.warn("Could not determine valid dayPart for schedule option:", option);
            return [];
        }

        return [{
            date: parsedDate.toFormat("yyyy-MM-dd"),
            dayPart: inferredDayPart,
            dayOfWeek: null,
            startTime,
            endTime
        }];
    });

    return normalizedOptions.map((opt) => ({
        date: opt.date, 
        dayPart: opt.dayPart as DayPart,
    }));
}